import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormRowDirective } from '../../directives/form-row.directive';
import { DialogService } from '../../services/dialog.service';
import { escapeDialog } from '../../utilities/dialog.util';
import { Button } from '../button/button.component';
import { InputRichText } from '../input-rich-text/input-rich-text.component';

export type RichTextDialogData = {
  label: string;
  value: string;
};

interface RichTextDialogForm {
  value: FormControl<string>;
}

@Component({
  templateUrl: './rich-text-dialog.component.html',
  styleUrl: './rich-text-dialog.component.scss',
  imports: [
    MatDialogContent,
    InputRichText,
    Button,
    ReactiveFormsModule,
    FormRowDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class RichTextDialog implements OnInit {
  readonly form: FormGroup<RichTextDialogForm>;

  readonly data: RichTextDialogData = inject(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.form = this.formBuilder.nonNullable.group<RichTextDialogForm>({
      value: this.formBuilder.nonNullable.control(this.data.value || ''),
    });
  }

  ngOnInit(): void {
    escapeDialog(this.dialogRef, this.destroyRef, () => this.onCancel());
  }

  /**
   * Handle form submission.
   */
  onSubmit(): void {
    const { value } = this.form.getRawValue();
    this.dialogRef.close(value);
  }

  /**
   * Handle dialog cancellation and ask for confirmation in case of edited
   * values.
   */
  onCancel(): void {
    if (this.form.pristine) {
      this.dialogRef.close();

      return;
    }

    const dialogRef = this.dialogService.showConfirmDialog();

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((closeConfirmed) => {
        if (closeConfirmed) {
          this.dialogRef.close();
        }
      });
  }

  getFormControl = (name: keyof RichTextDialogForm): FormControl =>
    this.form.get(name) as FormControl;
}
